<template>
  <v-app id="app" :style="{ background: 'var(' + appBackground + ')' }">
    <v-app-bar ref="appBarEl"
      :style="{background: toolbarGradient}"
      app
      dark
      flat
      :clipped-left="!rtl"
      :clipped-right="rtl"
      v-show="toolbar"
    >
      <v-app-bar-nav-icon
        id="nav-drawer-toggle"
        dark
        @click.stop="drawer = !drawer"
      >
      </v-app-bar-nav-icon>

      <v-spacer v-if="!isScreenLarge"></v-spacer>

      <v-toolbar-title>
        <v-img
          src="img/logo/beige_logo.png"
          max-width="130px"
          contain
        >
        </v-img>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <connection-indicator class="me-5"></connection-indicator>

      <v-btn id="search-conditions" @click="showSearchModal=true" icon
        class="search-conditions-btn"
        :title="$t('conditions_covered.title')"
      >
        <v-icon>{{ icons.mdiStethoscope }}</v-icon>
      </v-btn>

      <v-btn
        id="change-language"
        @click.stop="drawer = !drawer"
        icon
        :title="$t('app.nav.menu.language.title')"
      >
        <v-icon>{{ icons.mdiWeb }}</v-icon>
      </v-btn>

      <v-btn id="pause" v-if="encounterIsActive && showPauseButton" @click="showPauseModal=true" icon>
        <v-icon>{{ icons.mdiPause }}</v-icon>
      </v-btn>

      <v-btn id="information" v-if="false" icon>
        <v-icon>{{ icons.mdiInformationVariant }}</v-icon>
      </v-btn>
    </v-app-bar>

    <scroll-arrow
      id="app-scroll-arrow"
      :show="hasOverflow"
      :style="scrollArrowStyle"
      position="fixed"
      :arrowAlignment="arrowAlignment"
      color="rgba(178, 194, 198)"
      v-on:scrollDown="scrollDown"
      v-on:scrollUp="scrollUp"
    ></scroll-arrow>

    <modal
      tokenType="pause"
      coverBackground
      agreeTracked
      :show="showPauseModal"
      @cancel="showPauseModal=false"
      @agree="saveEncounterAndQuit"
    ></modal>
    <search-modal 
      :show="showSearchModal" 
      @close="showSearchModal=false"
      @open-suggestion-form="showSuggestionForm=true"
    ></search-modal>

    <user-suggestion-form v-model="showSuggestionForm" />

    <v-content ref="contentEl" style="overflow:auto;">
      <v-img
        v-if="isOnStart"
        contain
        class="background-img"
        :style="landingImageStyle"
        src="img/landing/particle_man.webp"
      ></v-img>
      <v-container id="top-container" class="top-responsive d-flex flex-column align-content-start" ref="topEl">
        <app-status ref="appStatusEl"></app-status>
      </v-container>
      <v-container
        id="content-container"
        :style="containerStyle"
        :class="containerClass"
        >
        <router-view
          :maxPanelHeight="this.maxHeight(panelOffset)"
          :navPadding="navTotalOffset"
          :style="routerStyle"
          >
        </router-view>
        <bottom v-show="showNav" ref="bottomEl" class="nav-responsive" ></bottom>
      </v-container>
    </v-content>

    <v-overlay
      opacity="1"
      :value="!dialog"
      eager
      color="var(--black-tone-background)"
      class="overlay"
    >
      <v-card
        class="loading-dialog"
        flat
      >
        <v-card-text class="d-flex flex-column align-center pa-6">
          <img
            :src="loadingLogo"
            class="loading-logo mb-6"
            alt=""
          >
          <v-progress-linear
            indeterminate
            rounded
            color="white"
            background-color="var(--primary-blue)"
            background-opacity="0.2"
            class="mb-3"
          ></v-progress-linear>
          <mrx-translation
            class="loading-label"
            :token="`app.loading.title`"
          ></mrx-translation>
        </v-card-text>
      </v-card>
    </v-overlay>

    <v-navigation-drawer
      v-model="drawer"
      :disable-route-watcher="true"
      :clipped="isScreenLarge"
      app
      :right="rtl"
      :style="{height: this.availableScreenHeight + 'px'}"
      >
      <drawer v-on:navToggle="drawer = !drawer"></drawer>
    </v-navigation-drawer>

  </v-app>

</template>
<script>
  import drawer from '@/components/layouts/Drawer'
  import bottom from '@/components/layouts/Bottom'
  import AppStatus from '@/components/layouts/AppStatus'
  import Modal from '@/components/layouts/Modal'
  import ScrollArrow from '@/components/layouts/ScrollArrow.vue'
  import searchModal from '@/components/layouts/SearchModal.vue'
  import UserSuggestionForm from '@/components/layouts/UserSuggestionForm.vue'
  import ConnectionIndicator from '@/components/layouts/ConnectionIndicator.vue'
  import icons from '@/mixins/icons'
  import ui from '@/mixins/ui'
  import scrollArrow from '@/mixins/scrollArrow'
  import { mapActions, mapState } from 'pinia'
  import { useSavedEncountersStore } from '@/stores/savedEncounters'
  import { useLanguageStore } from '@/stores/language'
  import { useTestModeStore } from '@/stores/testMode'
  import { useRegistrationStore } from '@/stores/registration'
  import { usePartnerConfigStore } from '@/stores/partnerConfig'
  import { usePreferenceStore } from '@/stores/preference'
  import { useTrackStore } from '@/stores/track'
  import { useEncounterStore } from '@/stores/encounter'
  import { usePatientStore } from '@/features/text-mode/stores/patient.store'
  import { useOverallAssessmentStore } from '@/features/overall-assessment/stores/overallAssessment'
  import { initializeApp } from '@/utils/appInit'
  import { clearActiveTextModeContext } from '@/features/text-mode/analytics/text-mode-context'

  export default {
    components: { drawer, bottom, ScrollArrow, AppStatus, Modal, searchModal, UserSuggestionForm, ConnectionIndicator },
    mixins: [ icons, ui, scrollArrow ],
    name: 'App',
    data() {
      return {
        drawer: false,
        dialog: false,
        topHeight: 0,
        toolbarHeight: 0,
        appStatusHeight: 0,
        updateNoticeOffset: 0,
        landingContentWidth: null,
        availableScreenHeight: 0,
        navHeight: 0,
        navBottomOffset: 0,
        topObserver: null,
        toolbarObserver: null,
        navObserver: null,
        contentObserver: null,
        showPauseModal: false,
        showSearchModal: false,
        showSuggestionForm: false,
      }
    },

    computed: {
      isScreenLarge() {
        return (this.$vuetify.breakpoint.lgAndUp);
      },
      toolbar() {
        return (this.$route.meta.top === undefined || this.getMode('show')) ? true : false;
      },
      showNav() {
        return this.$route.meta.show !== false;
      },
      centeredRoute() {
        return this.$route.meta.position === 'center';
      },
      panelOffset() {
        let panelLimit = 0;
        let progressMargin = this.navBottomOffset*2;

        panelLimit = this.toolbarHeight + this.appStatusHeight;

        // nav bar overlaps non-centered routes so its height only matters for centered ones
        if (this.centeredRoute && this.showNav) {
          panelLimit = panelLimit + this.navHeight + (progressMargin);
        }

        return panelLimit;
      },
      navTotalOffset() {
        return (this.showNav) ? this.navHeight + (this.navBottomOffset*2) : 0
      },
      navOffsetFromBottom() {
        return this.navHeight + (this.navBottomOffset/2)
      },
      routerStyle() {
        if (this.centeredRoute) {
          return {
            marginTop: this.topHeight + 'px',
          }
        }
        else return {}
      },
      containerStyle() {
        const bottomPadding = this.centeredRoute && this.showNav ? "96px !important" : "0px"
        const landingWidth = this.isOnStart && this.landingContentWidth
          ? `${this.landingContentWidth}px`
          : undefined

        return {
          "--update-notice-offset": `${this.updateNoticeOffset}px`,
          height: "100%",
          paddingBottom: bottomPadding,
          width: landingWidth,
          minWidth: landingWidth,
          maxWidth: landingWidth,
        }
      },
      containerClass() {
        const alignment = this.centeredRoute ? "align-center centered-panel-responsive" : "align-end"
        const landingWidth = this.isOnStart ? " landing-content-responsive" : ""

        return "d-flex flex-wrap panel-responsive pa-0 " + alignment + landingWidth
      },
      scrollArrowStyle() {
        return {
          height: "100vh",
          minWidth: "100%",
          paddingTop: this.toolbarHeight + 'px',
          paddingLeft: this.drawer && this.isScreenLarge ? '256px' : '0px'
        }
      },
      theme(){
        return (this.$vuetify.theme.dark) ? 'dark' : 'light'
      },
      appBackground() {
        if (['Login', 'Language', 'Setup'].includes(this.$route.name)) {
          return "--black-tone-background"
        }

        let tone = this.skinTone

        if (tone === 'black') {
          return "--black-tone-background"
        } else {
          return "--primary-blue"
        }
      },
      toolbarGradient() {
        let tone = this.skinTone

        if (tone === 'black') {
          return 'linear-gradient(180deg, RGBA(85, 156, 180, 1) 0%, 30%, rgba(85, 156, 180, 0) 100%)'
        } else {
          return 'linear-gradient(180deg, RGBA(6, 25, 30, 1) 0%, rgba(6, 25, 30, 0) 100%)'
        }
      },
      isOnStart() {
        return (this.$route.name === 'Start')
      },
      landingImageStyle() {
        const topOffset = this.appStatusHeight - 75

        return {
          top: `${topOffset}px`,
          height: `calc(100% - ${topOffset}px)`,
        }
      },
      loadingLogo() {
        return this.logo || '/branding/start/default.png'
      },
      showPauseButton() {
        return Boolean(this.encounterPauseResume)
          && this.identifyingDetailsCaptured
          && !this.encounterIsSaved
      },
      encounterIsSaved() {
        return this.journeyStage === 'complete'
      },
      identifyingDetailsCaptured() {
        const patient = this.textModePatient

        const hasIdentifier = Boolean(patient?.id)
        const hasAge = patient?.ageInDays != null
        const hasGender = Boolean(patient?.sex)

        return hasIdentifier && hasAge && hasGender
      },
      currentUserId() {
        try {
          return this.getResponse('user_id', 'number')?.value || null;
        } catch (e) {
          return null;
        }
      },
      ...mapState(useTrackStore, { encounterIsActive: 'active', trackSettings: 'settings' }),
      ...mapState(useOverallAssessmentStore, { journeyStage: 'stage' }),
      ...mapState(useEncounterStore, { encounterUuid: 'uuid' }),
      ...mapState(useLanguageStore, ['rtl']),
      ...mapState(useTestModeStore, { getMode: 'get' }),
      ...mapState(useRegistrationStore, ['hrId', 'registrationId']),
      ...mapState(usePartnerConfigStore, ['skinTone', 'encounterPauseResume', 'logo']),
      ...mapState(usePreferenceStore, ['getResponse']),
      ...mapState(usePatientStore, { textModePatient: 'patient' }),
    },

    methods: {
      async init() {
        this.dialog = await initializeApp()
        
        if (this.currentUserId) {
          this.identifyCurrentUser(this.currentUserId, 'session_resume')
        }
      },
      identifyCurrentUser(userId, loginMethod = 'panel_input') {
        try {
          const deviceId = this.$getDeviceId()
          const userType = this.trackSettings('audience') === 'chw' ? 'professional' : 'consumer'
          const registrationId = this.registrationId
          const hrId = this.hrId
          
          const previousUserId = localStorage.getItem('last_user_id')
          if (previousUserId && previousUserId !== userId.toString()) {
            this.$track(this.$analyticsEvents.USER_ID_CHANGED, {
              previous_user_id: previousUserId,
              new_user_id: userId,
              device_id: deviceId
            })
          }
          
          this.$track(this.$analyticsEvents.USER_IDENTIFIED, {
            user_id: userId,
            device_id: deviceId,
            user_type: userType,
            login_method: loginMethod
          })
          
          this.$identifyUser(userId, {
            user_type: userType,
            registration_id: registrationId,
            hr_id: hrId,
            device_id: deviceId
          })
          
          localStorage.setItem('last_user_id', userId.toString())
        } catch (error) {
          // Silently fail if user tracking fails
        }
      },
      saveEncounterAndQuit() {
        this.save(this.$route.fullPath)
        this.showPauseModal = false
        this.$track(this.$analyticsEvents.ENCOUNTER_PAUSE, {
          component: 'App',
          encounter_id: this.encounterUuid,
          paused_route: this.$route.path,
          journey_stage: this.journeyStage,
        })
        clearActiveTextModeContext()
        this.$router.push({ name: 'Start' })
      },
      onWindowScroll(e) {
        // readjusts available screen height for mobile when url bar disappears
        this.availableScreenHeight = window.innerHeight
        this.onScroll(e)
      },
      onWindowResize() {
        this.availableScreenHeight = window.innerHeight
        this.onContentResize()
      },
      onContentResize() {
        this.onScrollContentResize(this.navOffsetFromBottom)
      },
      onTopResize() {
        if (this.$refs.appStatusEl) {
          this.appStatusHeight = this.$refs.appStatusEl.$el.clientHeight;
          this.updateNoticeOffset = this.$refs.appStatusEl.$el.querySelector('#update-notice')
            ? this.appStatusHeight
            : 0;
        }

        this.topHeight = this.appStatusHeight;
        this.updateLandingContentWidth()
      },
      updateLandingContentWidth() {
        const banner = this.$refs.appStatusEl?.$el?.querySelector('.warning-banner')
        this.landingContentWidth = banner?.getBoundingClientRect().width || null
      },
      onToolbarResize() {
        if (this.$refs.appBarEl) {
          this.toolbarHeight = this.$refs.appBarEl.$el.offsetHeight
        }
      },
      onNavResize() {
        let navEl = this.$refs.bottomEl
        if (navEl) {
          this.navHeight = navEl.$el.offsetHeight
          this.navBottomOffset = 40
        }
      },
      scrollUp() {
        window.scrollTo(0, 0)
      },
      scrollDown() {
        const contentEl = this.$refs.contentEl.$el
        window.scrollTo(0, contentEl.scrollHeight)
      },
      ...mapActions(useSavedEncountersStore, ['save']),
    },
    mounted() {
      window.addEventListener('scroll', this.onWindowScroll)
      window.addEventListener('resize', this.onWindowResize)

      this.topObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.onTopResize())
      })
      this.toolbarObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.onToolbarResize())
      })
      this.navObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.onNavResize())
      })
      this.contentObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.onContentResize())
      })

      if (this.$refs.appStatusEl?.$el) {
        this.topObserver.observe(this.$refs.appStatusEl.$el)
      }
      if (this.$refs.appBarEl?.$el) {
        this.toolbarObserver.observe(this.$refs.appBarEl.$el)
      }
      if (this.$refs.bottomEl?.$el) {
        this.navObserver.observe(this.$refs.bottomEl.$el)
        this.observeForIntersection(this.$refs.bottomEl.$el)
      }
      if (this.$refs.contentEl?.$el) {
        this.observedElement = this.$refs.contentEl.$el
        this.contentObserver.observe(this.$refs.contentEl.$el)
      }
      this.$nextTick(this.updateLandingContentWidth)

      try {
        this.$router.onReady(() => {
          this.init()
        })
      }
      catch (e) { console.log(e) }
    },
    beforeDestroy() {
      window.removeEventListener('scroll', this.onWindowScroll)
      window.removeEventListener('resize', this.onWindowResize)
    },
    watch: {
      isOnStart(isOnStart) {
        if (isOnStart) this.$nextTick(this.updateLandingContentWidth)
      },
      currentUserId(newUserId, oldUserId) {
        if (newUserId && newUserId !== oldUserId) {
          this.identifyCurrentUser(newUserId, 'panel_input')
        }
      },
      '$route': {
        handler: function(route) {
          if (route.meta.title) {
            this.$headful({ title: route.meta.title })
          }
          else {
            this.$headful({ title: route.name })
          }
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>

<style scoped>
  .v-main {
    overflow: visible !important;
  }
  .search-conditions-btn {
    background-color: #FFC107;
    margin-right: 8px;
  }
  .search-conditions-btn:hover {
    background-color: #FFD54F;
  }

  .centered-panel-responsive {
    width: 100%;
  }

  .background-img {
    max-height: 100%;
    max-width: 100%;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, 0);
    z-index: 0;
    pointer-events: none;
  }

  .loading-dialog {
    background: transparent !important;
    box-shadow: none !important;
    min-height: fit-content;
    width: 100%;
  }

  .loading-logo {
    display: block;
    height: 96px;
    max-width: 320px;
    object-fit: contain;
    width: min(80%, 320px);
  }

  .loading-label {
    color: var(--deep-blue);
    font-size: 16px;
  }

  .loading-dialog >>> .v-progress-linear {
    width: 180px;
  }

  .overlay {
    align-items: center;
    padding: 24px;
  }

  .overlay >>> .v-overlay__content {
    width: 100%;
    min-height: fit-content;
  }

  .top-responsive {
    height: 100%;
    max-height: 50vh;
    max-width: 100vw;
    min-height: 0px;
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0);
    padding: 0px;
  }

  @media screen and (min-width: 750px) {
    .panel-responsive {
      min-width: 400px;
      max-width: 750px;
    }

    .nav-responsive {
      max-width: 675px;
    }

    .centered-panel-responsive {
      width: fit-content;
    }

    .top-responsive {
      max-width: 750px;
    }

    .overlay >>> .v-overlay__content {
      max-width: 670px;
    }
  }

</style>
